import request from './common/request'
import qs from 'qs'
import { MERCHANT_CERTIFICATE_GENERATION_URI } from './urls'

/**
 * 证书生成记录分页查询
 * GET /merchant/simbart/certificate/generation/query
 * @param {Object} params
 * @param {number} params.page 页码，从 1 开始
 * @param {number} params.pageSize 每页条数
 * @param {string} [params.generateStatus] 生成状态，如 SUCCESS
 * @param {string} [params.certificateName] 批次名称模糊查询
 * @returns {Promise<*>}
 */
export const query = async (params = {}) => {
  const queryParams = {
    page: params.page != null ? params.page : 1,
    pageSize: params.pageSize != null ? params.pageSize : 10
  }
  if (params.generateStatus != null && params.generateStatus !== '') {
    queryParams.generateStatus = params.generateStatus
  }
  const certificateName = params.certificateName != null ? String(params.certificateName).trim() : ''
  if (certificateName) {
    queryParams.certificateName = certificateName
  }
  return request({
    url: `${MERCHANT_CERTIFICATE_GENERATION_URI}/query`,
    method: 'GET',
    params: queryParams,
    paramsSerializer: (p) => qs.stringify(p, { skipNulls: true })
  })
}

/**
 * 创建证书生成记录（须带关联报名）
 * POST /merchant/simbart/certificate/generation
 * @param {Object} data
 * @param {number} data.templateId 奖状模板 ID
 * @param {number} data.projectId 评审项目 ID（对应库表 project_id）
 * @param {string} data.certificateName 批次名称
 * @param {number[]} data.entryValueIds 报名 ID 列表，至少 1 个
 * @returns {Promise<*>}
 */
export const create = async (data = {}) => {
  const entryValueIds = Array.isArray(data.entryValueIds)
    ? data.entryValueIds
      .map((id) => Number(id))
      .filter((id) => !isNaN(id))
    : []
  const body = {
    templateId: Number(data.templateId),
    projectId: Number(data.projectId),
    certificateName: String(data.certificateName || '').trim(),
    entryValueIds
  }
  return request({
    url: MERCHANT_CERTIFICATE_GENERATION_URI,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json;charset=UTF-8'
    },
    data: body
  })
}

/**
 * 生成记录详情
 * GET /merchant/simbart/certificate/generation/{id}
 */
export const findById = async (id) => {
  return request({
    url: `${MERCHANT_CERTIFICATE_GENERATION_URI}/${id}`,
    method: 'GET'
  })
}

/**
 * 更新生成记录关联报名（移除人员等）
 * PUT /merchant/simbart/certificate/generation/{id}
 */
export const updateEntryValueIds = async (id, data = {}) => {
  const entryValueIds = Array.isArray(data.entryValueIds)
    ? data.entryValueIds
      .map((val) => Number(val))
      .filter((val) => !isNaN(val))
    : []
  return request({
    url: `${MERCHANT_CERTIFICATE_GENERATION_URI}/${id}`,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json;charset=UTF-8'
    },
    data: { entryValueIds }
  })
}

/**
 * 更新批次与关联报名
 * PUT /merchant/simbart/certificate/generation/{id}/entries
 * @param {number|string} id 生成记录 ID
 * @param {Object} data
 * @param {string} [data.operation] ADD 末尾追加；DELETE 按 id 删除关联行
 * @param {number[]} [data.entryValueIds] ADD/DELETE 时必填
 * @param {string} [data.certificateName] 改批次名称
 * @param {number} [data.templateId] 改模版
 * @returns {Promise<*>}
 */
export const updateEntries = async (id, data = {}) => {
  const body = {}
  if (data.certificateName != null && String(data.certificateName).trim() !== '') {
    body.certificateName = String(data.certificateName).trim()
  }
  if (data.templateId != null && data.templateId !== '') {
    const templateId = Number(data.templateId)
    if (!isNaN(templateId)) {
      body.templateId = templateId
    }
  }
  if (data.operation != null && data.operation !== '') {
    body.operation = String(data.operation).toUpperCase()
    body.entryValueIds = Array.isArray(data.entryValueIds)
      ? data.entryValueIds
        .map((val) => Number(val))
        .filter((val) => !isNaN(val))
      : []
  }
  return request({
    url: `${MERCHANT_CERTIFICATE_GENERATION_URI}/${id}/entries`,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json;charset=UTF-8'
    },
    data: body
  })
}

/**
 * 删除证书生成记录
 * DELETE /merchant/simbart/certificate/generation/{id}
 * @param {number|string} id 生成记录 ID
 * @returns {Promise<*>}
 */
export const deleteById = async (id) => {
  return request({
    url: `${MERCHANT_CERTIFICATE_GENERATION_URI}/${id}`,
    method: 'DELETE'
  })
}

/**
 * 开始生成 PDF
 * POST /merchant/simbart/certificate/generation/{id}/generate
 * @param {number|string} id 生成记录 ID
 * @returns {Promise<*>} data 含 generateStatus、combinedPdfUrl、entries 等
 */
export const generate = async (id) => {
  return request({
    url: `${MERCHANT_CERTIFICATE_GENERATION_URI}/${id}/generate`,
    method: 'POST'
  })
}

/**
 * 发布（含再次发布）
 * POST /merchant/simbart/certificate/generation/{id}/publish
 * 前置：generateStatus=SUCCESS 且 fileIsLatest=1；已发布批次可再次调用
 * @param {number|string} id 生成记录 ID
 * @returns {Promise<*>} data 同创建响应；发布后 published=1、publishedAt 有值
 */
export const publish = async (id) => {
  return request({
    url: `${MERCHANT_CERTIFICATE_GENERATION_URI}/${id}/publish`,
    method: 'POST'
  })
}

/**
 * 撤销发布
 * POST /merchant/simbart/certificate/generation/{id}/unpublish
 * @param {number|string} id 生成记录 ID
 * @returns {Promise<*>}
 */
export const unpublish = async (id) => {
  return request({
    url: `${MERCHANT_CERTIFICATE_GENERATION_URI}/${id}/unpublish`,
    method: 'POST'
  })
}

export default {
  query,
  create,
  findById,
  updateEntryValueIds,
  updateEntries,
  deleteById,
  generate,
  publish,
  unpublish
}



// WEBPACK FOOTER //
// ./src/api/merchant-certificate-generation.js